/**
 * TrendSparkline — small inline 4-week line chart for activity metrics.
 *
 * Pure inline SVG, no chart lib. Renders a single series; pair multiple to
 * stack. Brand-locked stroke colors (navy / red).
 */
export interface TrendSparklineProps {
  values: number[];
  width?: number;
  height?: number;
  stroke?: string;
  fill?: string;
  ariaLabel?: string;
}

export function TrendSparkline({
  values,
  width = 120,
  height = 32,
  stroke = "#0B0B0F",
  fill = "rgba(15,23,42,0.08)",
  ariaLabel,
}: TrendSparklineProps) {
  if (!values || values.length === 0) {
    return (
      <svg width={width} height={height} role="img" aria-label={ariaLabel ?? "no data"}>
        <line x1={0} y1={height / 2} x2={width} y2={height / 2} stroke="#E5E5E7" strokeDasharray="2 2" />
      </svg>
    );
  }
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const stepX = values.length > 1 ? width / (values.length - 1) : width / 2;
  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return [x, y];
  });
  const path = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${path} L${(points[points.length - 1][0]).toFixed(1)},${height} L0,${height} Z`;
  const last = points[points.length - 1];
  return (
    <svg width={width} height={height} role="img" aria-label={ariaLabel ?? "trend"}>
      <path d={area} fill={fill} />
      <path d={path} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r={2} fill={stroke} />
    </svg>
  );
}

export default TrendSparkline;
